'use client';

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useJobQueueStore } from '../model/job-queue.store';
import { useSubscriptionLimits } from '@/features/subscription/model/subscription.selectors';
import { jobQueueService } from '@/shared/api/services/job-queue.service';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import type { RenderBlockReason } from '@/types/render';

/**
 * Server-authoritative render-gate reason from the WS/REST queue aggregate.
 *
 * This is the SINGLE source of truth for whether a render may proceed — the
 * API applies priority (tokens > queue) and emits exactly one reason (or
 * `null` = allowed). The render preflight maps this straight to a paywall;
 * no client-side renderability math (token balance, subscription state, slot
 * counting) decides this. Returns `null` until the first snapshot lands
 * (cold-start optimistically allows the tap; the server 422s if it must).
 *
 * ACCOUNT-LEVEL — the WS aggregate is user-scoped. For the render START gate use
 * {@link useBuildingRenderBlockReason}, which is per-building; this account-level
 * reason is still correct for the queue-full / slot UI.
 */
export function useRenderBlockReason(): RenderBlockReason {
  return useJobQueueStore((state) => state.queueAggregate?.blockReason ?? null);
}

/**
 * BUILDING-SCOPED render START gate reason for `projectId`.
 *
 * `block_reason` is per-building — a Facade Kit lifts the free/token gate on its
 * building only — but the WS queue aggregate is account-level and overwritten by
 * every snapshot. So the render screen fetches the gate scoped to its building
 * (`GET /v1/queue/status?project_id=`) and reads it here, keyed separately from
 * the global store. Reconciled by: WS queue:snapshot + reconnect (invalidate
 * `cacheKeys.queueStatus.all`), pack purchase / project update (same invalidate),
 * and window focus. Returns `null` (allow) until the first fetch lands.
 */
export function useBuildingRenderBlockReason(
  projectId: string | undefined
): RenderBlockReason {
  const { data } = useQuery({
    queryKey: projectId
      ? cacheKeys.queueStatus.building(projectId)
      : cacheKeys.queueStatus.all,
    queryFn: () => jobQueueService.getQueueStatus(projectId),
    enabled: !!projectId,
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  return data?.blockReason ?? null;
}

export interface QueueLimitStatus {
  isQueueFull: boolean;
  activeCount: number;
  queueLimit: number;
  hasQueueLimit: boolean;
  message: string;
}

/**
 * Render-slot quota status for the Generate button preflight.
 *
 * Reads `queueAggregate` from the WS-driven store (computed once per snapshot
 * in API `RenderLimitsService::getQueueStatus`) when present. Falls back to the
 * local count + plan-limit math during cold-start before the first WS snapshot
 * or `GET /v1/queue/status` lands. The local fallback never sees albums (they
 * have their own sliding-window quota and must not consume render slots).
 *
 * `isQueueFull` gates only on slot availability — a `canRender=false` due to
 * insufficient tokens is surfaced through the separate token-balance check,
 * not the queue-full paywall.
 */
export function useQueueLimit(): QueueLimitStatus {
  const queueAggregate = useJobQueueStore((state) => state.queueAggregate);
  const getProcessingRenderJobCount = useJobQueueStore(
    (state) => state.getProcessingRenderJobCount
  );
  const jobQueue = useJobQueueStore((state) => state.jobQueue);
  const limits = useSubscriptionLimits();

  const localActiveCount = useMemo(
    () => getProcessingRenderJobCount(),
    [getProcessingRenderJobCount, jobQueue]
  );
  const localPlanLimit = limits?.generations_queue || 0;

  const activeCount = queueAggregate?.activeRenders ?? localActiveCount;
  const queueLimit = queueAggregate?.queueLimit ?? localPlanLimit;
  const hasQueueLimit = queueLimit > 0;
  const isQueueFull = queueAggregate
    ? queueAggregate.availableSlots === 0
    : hasQueueLimit && activeCount >= queueLimit;

  const message = useMemo(() => {
    if (!hasQueueLimit) {
      return '';
    }
    if (isQueueFull) {
      return `Queue full (${activeCount}/${queueLimit}). Please wait for current renders to complete.`;
    }
    return `Queue: ${activeCount}/${queueLimit}`;
  }, [isQueueFull, activeCount, queueLimit, hasQueueLimit]);

  return {
    isQueueFull,
    activeCount,
    queueLimit,
    hasQueueLimit,
    message,
  };
}
